import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import ConversationListTable from 'components/tables/ConversationsListTable';
import { Button, Spinner } from 'react-bootstrap';
import Badge, { BadgeBg } from 'components/base/Badge';
import { Card, Col, Row } from 'react-bootstrap';
import { Dropdown } from 'react-bootstrap';
import AnalysisViewModal from 'pages/meetings/components/AnalysisViewModal';

import { useNavigate } from 'react-router-dom';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';

import { useTranslation } from 'react-i18next';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';


interface Conversation {
  id: number;
  name: string;
  contact: string;
  contact_id: number;
  response: string;
  date: string;
  status: string;
  transcription_lang: string;
  number_of_speakers: string;
}


interface ActionDropdownItemsProps {
  resource_id: number;
  status: string;
  handleAction: (row_id: number, command: string) => void;
}


const ActionDropdownItems: React.FC<ActionDropdownItemsProps> = ({ resource_id, status, handleAction }) => {
  return (
    <>
      <Dropdown.Item eventKey="1" onClick={() => handleAction(resource_id, 'analyze')} hidden={status !== 'get feedback'} >Analyze</Dropdown.Item>
      <Dropdown.Item eventKey="2" onClick={() => handleAction(resource_id, "view_analysis")} hidden={status !== 'ready'}>View Feedback</Dropdown.Item>
      <Dropdown.Item eventKey="2" onClick={() => handleAction(resource_id, "export_analysis")} hidden={status !== 'ready'} disabled>Export Feedback</Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item eventKey="4" className="text-danger" onClick={() => handleAction(resource_id, "delete")}>
        Remove
      </Dropdown.Item>
    </>
  );
};



const TeamConversations = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();


  const breadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: t('Dashboard'),
      url: '/dashboard/home'
    },
    {
      label: t('Meetings'),
      url: '/effective-communication'
    },
    {
      label: t('TeamConversations.page_title'),
      active: true
    }
  ];

  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysis, setAnalysis] = useState('');

  const conversationListTableColumns: ColumnDef<Conversation>[] = [
    {
      accessorKey: 'name',
      header: 'Audio File Name',
      cell: ({ row: { original } }) => {
        const { id, name } = original;
        return (
          <Link to={`/effective-communication/team/${id}`} className="fw-semi-bold">
            {name}
          </Link>
        );
      },
      meta: {
        cellProps: { className: 'white-space-nowrap py-4' },
        headerProps: { style: { width: '25%' } }
      }
    },


    {
      //header: t('ConversationsListView.table.Contact'),
      header: 'Contact',
      accessorKey: 'contact',
      meta: {
        headerProps: { style: { width: '10%' }, className: 'ps-3' }
      },
      cell: ({ row: { original } }) => {
        const { id, contact, contact_id, number_of_speakers } = original;

        /*
        let bg_color: BadgeBg = 'primary';
        if (status === 'ready') {
          bg_color = 'success';
          return (
            <Badge title='View feedback' variant="phoenix" bg={bg_color} onClick={() => handleDropdownItemClick(id, "view_analysis")} style={{ cursor: 'pointer' }}>
              {status}
            </Badge>
          );
        }

        if (status === 'edit transcript') {
          const title = 'Review Transcript';
          bg_color = 'danger';

          return (
            <Badge title={title} variant="phoenix" bg={bg_color}
              onClick={() => navigate(`/dashboard/automatic-transcription/conversation?resource_id=${id}`)}
              style={{ cursor: 'pointer' }}>
              {status}
            </Badge>
          );
        }
        */
        if (number_of_speakers === '-')
          return ("-");

        if (parseInt(number_of_speakers) > 2) {
          return (
            <Badge variant="phoenix" bg="success">
              Group
            </Badge>
          );
        }

        if (contact === null) {
          return (
            <Badge variant="phoenix" bg="warning" onClick={() => navigate(`/effective-communication/${id}`)}
              style={{ cursor: 'pointer' }}>
              {t("ConversationsListView.table.assign_contact")}
            </Badge>
          );
        }
        else
          return (
            <Link to={`/contacts/update-contact?contact_id=${contact_id}`} className="fw-semi-bold">
              {contact}
            </Link>
          );
      },
    },
    {
      header: 'Language',
      accessorKey: 'transcription_lang',
      meta: {
        cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
        headerProps: { style: { width: '10%' }, className: 'ps-3' }
      }
    },
    {
      header: 'Speakers',
      accessorKey: 'number_of_speakers',
      meta: {
        cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
        headerProps: { style: { width: '10%' }, className: 'ps-3' }
      }
    },
    {
      header: 'State',
      accessorKey: 'status',
      meta: {
        //cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
        headerProps: { style: { width: '15%' }, className: 'ps-3' }
      },
      cell: ({ row: { original } }) => {
        const { id, status /*, transcription_meta*/ } = original;
        let bg_color: BadgeBg = 'primary';
        if (status === 'ready') {
          bg_color = 'success';
          return (
            <Badge title='View feedback' variant="phoenix" bg={bg_color} onClick={() => handleDropdownItemClick(id, "view_analysis")} style={{ cursor: 'pointer' }}>
              {status}
            </Badge>
          );
        }

        if (status === 'edit transcript') {
          const title = 'Review Transcript';
          bg_color = 'danger';

          /*
          const onClickHandler = transcription_meta === 'English/American'
            ? () => handleDropdownItemClick(id, "analyze")
            : undefined; // Assign undefined if language doesn't match          
          */

          return (
            <Badge title={title} variant="phoenix" bg={bg_color}
              onClick={() => navigate(`/effective-communication/${id}`)}
              style={{ cursor: 'pointer' }}>
              {status}
            </Badge>
          );
        }

        return (
          <Link to={`/effective-communication/${id}`} className="fw-semi-bold">
            <Badge variant="phoenix" bg={bg_color}>
              {status}
            </Badge>
          </Link>
        );
      },
    },

    {
      header: 'Date',
      accessorKey: 'date',
      meta: {
        cellProps: { className: 'ps-3 text-900 py-4' },
        headerProps: { style: { width: '12%' }, className: 'ps-3' }
      }
    },

    /*
    {
      id: 'reflections',
      header: 'Reflections',
      accessorKey: 'reflections',
      meta: {
        cellProps: { className: 'ps-8 py-4' },
        headerProps: { style: { width: '10%' }, className: 'ps-8' }
      }
    },
    */
    {
      id: 'action',
      cell: ({ row: { original } }) => {
        //console.log(original);

        const { id, status } = original;

        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems resource_id={id} status={status} handleAction={handleDropdownItemClick} />
            </RevealDropdown>
          </RevealDropdownTrigger>
        )
      },

      /*
      cell: ({ row: { original } }) => {
        const { id } = original;
        return (
          <Link to="#!" className="fw-semi-bold">
            #{id}
          </Link>
        );
      */

      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-end' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: conversations,
    columns: conversationListTableColumns,
    pageSize: 6,
    pagination: true,
    sortable: true
  });


  const analyze = async (row_id: number) => {
    try {
      // Send the file to the backend using Axios
      // ALT+96 -> `
      const response = await apiClient.post(`/v1/api/analyze/?resource_id=${row_id}&lang=EN`, {}, {
        headers: {

        },
        withCredentials: true,
      });
      //console.log('Analyze transcription started successfully:', response.data);
      fetchData('group');

    } catch (error) {
      console.error('Error analyzing file:', error);
    }
  }

  const deleteResource = async (resource_id: number) => {
    try {
      // Send the file to the backend using Axios
      // ALT+96 -> `
      const response = await apiClient.delete(`/v1/conversations/${resource_id}`, {
        headers: {

        },
        withCredentials: true,
      });
      if (response.status === 200) {
        console.log('Deleted resource successfully:', response.data);
      }

      fetchData('group');

    } catch (error) {
      console.error('Error deleteting a resource:', error);
    }
  }

  const handleDropdownItemClick = async (resource_id: number, command: string) => {
    console.log("handleDropdownItemClick " + resource_id + " - " + command);

    if (command === 'analyze')
      analyze(resource_id);

    if (command === 'view_analysis') {

      const response = await apiClient.get(
        `/v1/analysis/resource/${resource_id}`,
        {
          headers: {
            'accept': 'application/json',

          }
        }
      );
      setAnalysis(response.data['result']);
      setShowAnalysisModal(true);


    }


    if (command === 'delete') {
      deleteResource(resource_id);
    }
  };


  const fetchData = async (type: string) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        '/v1/conversations/type/' + type,
        {
          headers: {
            'accept': 'application/json',

          }
        }
      );
      setConversations(response.data);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    fetchData('group');
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    // Trigger click event on file input element
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; // Get the first selected file
    // Perform file upload logic here, such as sending the file to your backend
    //console.log('Selected file:', file);
    // Create a FormData object

    if (file) {
      // Create a FormData object
      const formData = new FormData();
      formData.append('file', file);
      setIsUploading(true);

      const updatedConv = [...conversations];
      const newFile: Conversation = {
        id: -1, name: file.name,
        contact: '', contact_id: 0, response: '',
        date: '', status: 'uploading', transcription_lang: '',
        number_of_speakers: ''
      }

      updatedConv.push(newFile);
      setConversations(updatedConv);

      try {
        // Send the file to the backend using Axios
        const response = await apiClient.post<{ filename: string; size: number }>('/v1/conversations/upload/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',

          }
        });
        //console.log('File uploaded successfully:', response.data);
        // refresh data
        fetchData('group');
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        setIsUploading(false);

        if (fileInputRef.current)
          fileInputRef.current.value = ''; // Clear the file input value
      }
    }

  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>

      <AnalysisViewModal analysis={analysis}
        show={showAnalysisModal}
        handleClose={() => setShowAnalysisModal(false)}
      />

      {/* Hidden file input element */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />

      <PageBreadcrumb items={breadcrumbItems} />
      <Row className="mt-3  mb-3">
        <Col sm={12} md={12} lg={12}>
          <Card className='border border-light rounded-5' >
            <Card.Body>
              <AdvanceTableProvider {...table}>
                <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-end">


                  {/*
                    <Button className="btn btn-primary px-5" onClick={() => fetchData()}>
                      <FontAwesomeIcon icon={faRefresh} className="me-2" /> {t('Refresh')}
                    </Button>
      */}

                  <Button className="btn btn-primary px-5" onClick={handleButtonClick}>
                    {isUploading && (<>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      /> {t('Uploading in progress...')}</>)}

                    {!isUploading && (<><FontAwesomeIcon icon={faPlus} className="me-2" /> {t('Upload new audio file')}</>)}
                  </Button>

                </div>
                <ConversationListTable />
              </AdvanceTableProvider>

            </Card.Body>
          </Card>
        </Col>
      </Row>

    </div >
  );
};

export default TeamConversations;
